import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import Layout from './components/common/Layout';
import Dashboard from './components/dashboard/Dashboard';
import PatientList from './components/patients/PatientList';
import PatientForm from './components/patients/PatientForm';
import PatientDetail from './components/patients/PatientDetail';
import CaseList from './components/cases/CaseList';
import CaseForm from './components/cases/CaseForm';
import CaseDetail from './components/cases/CaseDetail';
import PhotoGallery from './components/photos/PhotoGallery';
import PhasePhotoManager from './components/photos/PhasePhotoManager';
import PhotoComparison from './components/photos/PhotoComparison';
import SmileDesigner from './components/photos/SmileDesigner';
import ExamOverview from './components/exam/ExamOverview';
import ExamForm from './components/exam/ExamForm';
import UserManagement from './components/admin/UserManagement';
import AuditLogViewer from './components/admin/AuditLogViewer';
import TwoDChecks from './components/clinical/TwoDChecks';
import MaxillaryIncisalEdge from './components/clinical/MaxillaryIncisalEdge';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Patient Routes */}
          <Route
            path="/patients"
            element={
              <ProtectedRoute requirePermission="viewPatients">
                <Layout>
                  <PatientList />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/new"
            element={
              <ProtectedRoute requirePermission="createPatients">
                <Layout>
                  <PatientForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId"
            element={
              <ProtectedRoute requirePermission="viewPatients">
                <Layout>
                  <PatientDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/edit"
            element={
              <ProtectedRoute requirePermission="updatePatients">
                <Layout>
                  <PatientForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Case Routes */}
          <Route
            path="/cases"
            element={
              <ProtectedRoute requirePermission="viewCases">
                <Layout>
                  <CaseList />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/new"
            element={
              <ProtectedRoute requirePermission="createCases">
                <Layout>
                  <CaseForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/:caseId"
            element={
              <ProtectedRoute requirePermission="viewCases">
                <Layout>
                  <CaseDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/:caseId/edit"
            element={
              <ProtectedRoute requirePermission="updateCases">
                <Layout>
                  <CaseForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Exam Routes */}
          <Route
            path="/patients/:patientId/cases/:caseId/exam"
            element={
              <ProtectedRoute requirePermission="viewCases">
                <Layout>
                  <ExamOverview />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/:caseId/exam/:sectionId"
            element={
              <ProtectedRoute requirePermission="viewCases">
                <Layout>
                  <ExamForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Photo Routes */}
          <Route
            path="/photos"
            element={
              <ProtectedRoute requirePermission="viewPhotos">
                <Layout>
                  <PhotoGallery />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/:caseId/phases/:phaseId/photos"
            element={
              <ProtectedRoute requirePermission="viewPhotos">
                <Layout>
                  <PhasePhotoManager />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:patientId/cases/:caseId/compare"
            element={
              <ProtectedRoute requirePermission="viewPhotos">
                <Layout>
                  <PhotoComparison />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* AI Smile Mock-up */}
          <Route
            path="/patients/:patientId/cases/:caseId/smile-design"
            element={
              <ProtectedRoute requirePermission="viewPhotos">
                <Layout>
                  <SmileDesigner />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Clinical Workflow Routes */}
          <Route
            path="/2d-checks"
            element={
              <ProtectedRoute>
                <Layout>
                  <TwoDChecks />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/maxillary-incisal-edge"
            element={
              <ProtectedRoute>
                <Layout>
                  <MaxillaryIncisalEdge />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/users"
            element={
              <ProtectedRoute requirePermission="viewUsers">
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit"
            element={
              <ProtectedRoute requirePermission="viewAuditLogs">
                <Layout>
                  <AuditLogViewer />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Redirect unknown routes to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
